import React from 'react'
import moment from 'moment'
import type {GpsStatus} from './VehicleList'
import type {TimelineViaInput} from './jobTimeline'

export type VehicleDetailData = {
  itemId: number
  vehicleNumber: string
  requestId: string
  jobStatus?: string | null
  pickupLocation?: string | null
  deliveryLocation?: string | null
  atPickup?: string | Date | null
  dtPickup?: string | Date | null
  atDelivery?: string | Date | null
  dtDelivery?: string | Date | null
  viaLocationList?: TimelineViaInput[] | null
  speed: number | null
  gpsStatus: GpsStatus
  lastUpdateUnix?: number | null
  mileage?: string | number | null
  battery?: string | number | null
}

type Props = {
  open: boolean
  variant: 'drawer' | 'sheet'
  vehicle: VehicleDetailData | null
  isFollowing: boolean
  timelineOpen: boolean
  onClose: () => void
  onFocus: () => void
  onToggleFollow: () => void
  onToggleTimeline: () => void
}

const statusLabel = (status: GpsStatus) => {
  if (status === 'moving') return 'Moving'
  if (status === 'stopped') return 'Stopped'
  return 'No GPS'
}

const statusClass = (status: GpsStatus) => {
  if (status === 'moving') return 'vm-status-chip vm-status-chip--moving'
  if (status === 'stopped') return 'vm-status-chip vm-status-chip--stopped'
  return 'vm-status-chip vm-status-chip--no-gps'
}

export const VehicleDetailPanel: React.FC<Props> = ({
  open,
  variant,
  vehicle,
  isFollowing,
  timelineOpen,
  onClose,
  onFocus,
  onToggleFollow,
  onToggleTimeline,
}) => {
  if (!open || !vehicle) return null

  const lastUpdate =
    vehicle.lastUpdateUnix != null ? moment.unix(vehicle.lastUpdateUnix) : null

  return (
    <div
      className={[
        'vm-detail-panel',
        variant === 'drawer' ? 'vm-detail-panel--drawer' : 'vm-detail-panel--sheet',
        timelineOpen ? 'vm-detail-panel--timeline' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className='vm-detail-panel__header'>
        <div className='min-w-0 flex-grow-1'>
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <h4 className='fs-6 fw-bold text-gray-900 mb-0 text-truncate'>
              {vehicle.vehicleNumber}
            </h4>
            {vehicle.jobStatus ? (
              <span className='vm-infowindow__job-status'>{vehicle.jobStatus}</span>
            ) : null}
            <span className={statusClass(vehicle.gpsStatus)}>
              {statusLabel(vehicle.gpsStatus)}
            </span>
            <button
              type='button'
              className={`btn btn-sm btn-icon ${
                timelineOpen ? 'btn-primary' : 'btn-light'
              } vm-timeline-toggle`}
              title='Job timeline'
              aria-label='Toggle job timeline'
              aria-pressed={timelineOpen}
              onClick={onToggleTimeline}
            >
              <i className='bi bi-signpost-2'></i>
            </button>
          </div>
        </div>
        <button type='button' className='btn btn-sm btn-icon btn-light' onClick={onClose}>
          <i className='bi bi-x-lg'></i>
        </button>
      </div>

      <div className='vm-detail-panel__body'>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Request ID</span>
          <span className='vm-detail-field__value'>{vehicle.requestId || '—'}</span>
        </div>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Job status</span>
          <span className='vm-detail-field__value'>{vehicle.jobStatus || '—'}</span>
        </div>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Pickup</span>
          <span className='vm-detail-field__value'>{vehicle.pickupLocation || '—'}</span>
        </div>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Delivery</span>
          <span className='vm-detail-field__value'>{vehicle.deliveryLocation || '—'}</span>
        </div>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Speed</span>
          <span className='vm-detail-field__value'>
            {vehicle.speed != null ? `${vehicle.speed} km/h` : '—'}
          </span>
        </div>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Last update</span>
          <span className='vm-detail-field__value'>
            {lastUpdate
              ? `${lastUpdate.format('YYYY-MM-DD HH:mm:ss')} (${lastUpdate.fromNow()})`
              : '—'}
          </span>
        </div>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Mileage</span>
          <span className='vm-detail-field__value'>
            {vehicle.mileage != null && vehicle.mileage !== '' ? `${vehicle.mileage} km` : 'N/A'}
          </span>
        </div>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Battery</span>
          <span className='vm-detail-field__value'>
            {vehicle.battery != null && vehicle.battery !== '' ? `${vehicle.battery}%` : 'N/A'}
          </span>
        </div>
      </div>

      <div className='vm-detail-panel__actions'>
        <button type='button' className='btn btn-sm btn-light-primary' onClick={onFocus}>
          Focus
        </button>
        <button
          type='button'
          className={`btn btn-sm ${isFollowing ? 'btn-primary' : 'btn-light'}`}
          onClick={onToggleFollow}
        >
          {isFollowing ? 'Following' : 'Follow'}
        </button>
        <button type='button' className='btn btn-sm btn-light' onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  )
}
